import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../base-components/pai-button';
import PaiLink from '../base-components/pai-link';

interface Props {
  open: boolean;
  title: string;
  content: string | React.ReactNode;
  variant?: 'warning' | 'info' | 'success' | 'error';
  loading?: boolean;

  handleConfirm?(): void;

  handleClose?(): void;
}

const PaiConfirmDialog: React.FC<Props> = ({ open, title, content, variant = 'warning', loading, handleConfirm, handleClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={cx(classes.title, variant)}>
        <Typography className={classes.titleText}>{title}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {typeof content === 'string' ? <Typography className={classes.text}>{content}</Typography> : content}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} variant={'default'} underline={'always'} action={handleClose} />
        <PaiButton text={t('ok')} size={'small'} border={'rounded'} action={handleConfirm} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: `linear-gradient(to right, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
    '&.info': {
      background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    },
    '&.success': {
      background: `linear-gradient(to right, ${theme.palette.success.main}, ${theme.palette.success.light})`,
    },
    '&.error': {
      background: `linear-gradient(to right, ${theme.palette.error.main}, ${theme.palette.error.light})`,
    },
    '&.warning': {
      background: `linear-gradient(to right, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
    },
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.warning': {
      color: theme.palette.warning.main,
      fontWeight: 'bold',
    },
    '&.highlight': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default PaiConfirmDialog;
